import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { TableProps } from 'antd'
import { Table, message } from 'antd'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { SeedOrderStatus, SeedOrderApiRecord, DestinationType } from 'modules/domain/seedOrders/types'
import { getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import { listSeedOrders } from 'modules/domain/seedOrders/managers'
import { useHistory } from 'react-router-dom'
import { generatePath } from 'react-router-dom'
import { formatPhoneForDisplay } from 'modules/utils/formatPhoneForDisplay'
import Routes from '../route'
import { PedigreeTag } from '../../SeedTracker/SeedInventory/PedigreeTag'
import { seedTrackerVarietyLabel } from '../SeedOrderAdd'
import { formatSeedOrderFarmerLabel } from '../farmerOptions'
import { StatusLabel, rowCursorPointerStyle } from '../styles'

// Seed Order data type (table + detail / navigation)
export type SeedOrder = {
  id: string
  productCardId: string
  soPoSivNumber: string
  variety: string
  pedigree: SeedPedigree
  quantityMt: string
  originGrower: string
  destinationGrower: string
  originCompanyId: string
  destinationCompanyId: string
  lastUpdatedDate: string
  status: SeedOrderStatus
  orderNumber: string
  purchaseOrderNumber: string
  sivNumber: string
  createdDate: string
  originLocation: string
  originRegion: string
  originContactName: string
  originContactNumber: string
  originContactEmail: string
  destinationLocation: string
  destinationRegion: string
  destinationContactName: string
  destinationContactNumber: string
  destinationContactEmail: string
  destinationType?: DestinationType
  destinationFarmerName: string
  destinationFarmerLabel?: string
  destinationFarmerContactNumber: string
  destinationFarmerContactEmail: string
  destinationOtherDetails: string
  origin_current_mt?: string
  origin_requested_mt?: string
  origin_after_sale_mt?: string
}

function parseSeedOrderStatus(raw: string): SeedOrderStatus {
  const values = Object.values(SeedOrderStatus) as string[]
  if (values.includes(raw)) return raw as SeedOrderStatus
  return SeedOrderStatus.Pending
}

function formatSoPoSiv(row: SeedOrderApiRecord): string {
  const so = row.sales_order_number?.trim()
  const po = row.purchase_order_number?.trim()
  const siv = row.sales_invoice_number?.trim()
  const parts = [so ? `SO ${so}` : '', po ? `PO ${po}` : '', siv ? `SIV ${siv}` : ''].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

function s(v: string | null | undefined): string {
  if (v == null || v === '') {
    return '—'
  }
  return v
}

export function mapSeedOrderApiRecordToSeedOrder(
  row: SeedOrderApiRecord,
  varietyByProductCardId: Record<string, string>,
): SeedOrder {
  const so = row.sales_order_number?.trim() ?? ''
  const po = row.purchase_order_number?.trim() ?? ''
  const siv = row.sales_invoice_number?.trim() ?? ''
  const lastUpdated = row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'
  const created = row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'

  return {
    id: row.id,
    productCardId: row.product_card_id,
    soPoSivNumber: formatSoPoSiv(row),
    variety: varietyByProductCardId[row.product_card_id] ?? '—',
    pedigree: row.current_pedigree,
    quantityMt: row.quantity_mt,
    originGrower: row.origin_company?.official_name ?? '—',
    destinationGrower: row.destination_company?.official_name ?? '—',
    originCompanyId: row.origin_company_id,
    destinationCompanyId: row.destination_company_id,
    lastUpdatedDate: lastUpdated,
    status: parseSeedOrderStatus(row.order_status),
    orderNumber: so || '—',
    purchaseOrderNumber: po || '—',
    sivNumber: siv || '—',
    createdDate: created,
    originLocation: '—',
    originRegion: s(row.origin_region),
    originContactName: s(row.origin_contact_name),
    originContactNumber: s(formatPhoneForDisplay(row.origin_contact_number)),
    originContactEmail: s(row.origin_contact_email),
    destinationLocation: '—',
    destinationRegion: s(row.destination_region),
    destinationContactName: s(row.destination_contact_name),
    destinationContactNumber: s(formatPhoneForDisplay(row.destination_contact_number)),
    destinationContactEmail: s(row.destination_contact_email),
    destinationType: row.destination_type ?? undefined,
    destinationFarmerName: s(
      row.destination_farmer
        ? `${row.destination_farmer.first_name ?? ''} ${row.destination_farmer.last_name ?? ''}`.trim()
        : undefined,
    ),
    destinationFarmerContactNumber: s(formatPhoneForDisplay(row.destination_farmer_contact_number)),
    destinationFarmerLabel: s(row.destination_farmer ? formatSeedOrderFarmerLabel(row.destination_farmer) : undefined),
    destinationFarmerContactEmail: s(row.destination_farmer_contact_email),
    destinationOtherDetails: s(row.destination_other_details),
    origin_current_mt: row.origin_current_mt ?? undefined,
    origin_requested_mt: row.origin_requested_mt ?? undefined,
    origin_after_sale_mt: row.origin_after_sale_mt ?? undefined,
  }
}

const getStatusBackgroundColor = (status: SeedOrderStatus): string => {
  switch (status) {
    case SeedOrderStatus.Pending:
      return '#037BFF'
    case SeedOrderStatus.Approved:
      return '#2E7D32'
    case SeedOrderStatus.InTransit:
      return '#F57C00'
    case SeedOrderStatus.Completed:
      return '#20B02E'
    case SeedOrderStatus.Cancelled:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

export type SeedOrdersTableProps = {
  search?: string
  productCardId?: string
  currentPedigree?: string
  originCompanyId?: string
  destinationCompanyId?: string
  orderStatus?: string
}

const DEFAULT_PAGE_SIZE = 50

const SeedOrdersList: React.FC<SeedOrdersTableProps> = ({
  search,
  productCardId,
  currentPedigree,
  originCompanyId,
  destinationCompanyId,
  orderStatus,
}) => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SeedOrder[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const filterKey = `${search ?? ''}|${productCardId ?? ''}|${currentPedigree ?? ''}|${originCompanyId ?? ''}|${
    destinationCompanyId ?? ''
  }|${orderStatus ?? ''}`
  const prevFilterKeyRef = useRef(filterKey)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const ordersRes = await listSeedOrders({
        search: search?.trim() || undefined,
        product_card_id: productCardId || undefined,
        current_pedigree: currentPedigree || undefined,
        origin_company_id: originCompanyId || undefined,
        destination_company_id: destinationCompanyId || undefined,
        order_status: orderStatus || undefined,
        page,
        page_size: pageSize,
      })
      const trackersRes = await getSeedTrackersList({}, {}, 1, 5000)
      const varietyBy = Object.fromEntries((trackersRes.data ?? []).map((r) => [r.id, seedTrackerVarietyLabel(r)]))
      setData((ordersRes.data ?? []).map((row) => mapSeedOrderApiRecordToSeedOrder(row, varietyBy)))
      setTotal(ordersRes.total_count ?? 0)
    } catch (e) {
      console.error(e)
      message.error('Could not load seed orders.')
      setData([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [search, productCardId, currentPedigree, originCompanyId, destinationCompanyId, orderStatus, page, pageSize])

  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey
      if (page !== 1) {
        setPage(1)
        return
      }
    }
    void load()
  }, [filterKey, page, pageSize, load])

  const goToDetails = (record: SeedOrder) => {
    const path = generatePath(Routes.SeedOrderDetails, { id: record.id })
    history.push(path, { seedOrder: record, returnTo: { route: Routes.List } })
  }

  const columns: TableProps<SeedOrder>['columns'] = [
    {
      title: 'SO / PO / SIV #',
      dataIndex: 'soPoSivNumber',
      key: 'soPoSivNumber',
    },
    {
      title: 'Variety',
      dataIndex: 'variety',
      key: 'variety',
    },
    {
      title: 'Pedigree',
      dataIndex: 'pedigree',
      key: 'pedigree',
      render: (pedigree: SeedPedigree) => <PedigreeTag pedigree={pedigree} />,
    },
    {
      title: 'Quantity (MT)',
      dataIndex: 'quantityMt',
      key: 'quantityMt',
    },
    {
      title: 'Origin',
      dataIndex: 'originGrower',
      key: 'originGrower',
    },
    {
      title: 'Destination',
      dataIndex: 'destinationGrower',
      key: 'destinationGrower',
      render: (_value: string, record: SeedOrder) => {
        const destinationType = record.destinationType ?? DestinationType.Grower
        const details =
          destinationType === DestinationType.Farmer
            ? record.destinationFarmerLabel ?? record.destinationFarmerName
            : destinationType === DestinationType.Other
            ? record.destinationOtherDetails
            : record.destinationGrower
        const displayDetails =
          destinationType === DestinationType.Other && details.length > 60 ? `${details.slice(0, 60)}...` : details
        return (
          <div>
            <div style={{ fontStyle: 'italic' }}>{destinationType}</div>
            <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{displayDetails}</div>
          </div>
        )
      },
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'lastUpdatedDate',
      key: 'lastUpdatedDate',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: SeedOrderStatus) => (
        <StatusLabel backgroundColor={getStatusBackgroundColor(status)}>{status}</StatusLabel>
      ),
    },
  ]

  const rowKey: TableProps<SeedOrder>['rowKey'] = (record) => record.id

  return (
    <Table<SeedOrder>
      columns={columns}
      dataSource={data}
      rowKey={rowKey}
      loading={loading}
      onRow={(record) => ({
        onClick: () => goToDetails(record),
        style: rowCursorPointerStyle,
      })}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        position: ['bottomLeft'],
        onChange: (nextPage, nextPageSize) => {
          if (nextPageSize != null && nextPageSize !== pageSize) {
            setPageSize(nextPageSize)
            setPage(1)
          } else {
            setPage(nextPage)
          }
        },
      }}
    />
  )
}

export default SeedOrdersList
